import {
  all, call, put, select, take,
} from 'redux-saga/effects';
import {
  compose, find, get, getOr, identity, includes, isArray, last, mapValues, pickBy, reduce, split, toNumber, values,
} from 'lodash/fp';

import * as errorActions from '../../errors/actions';
import * as contactServices from '../contact/services';
import { FIREBASE_REF } from '../../constants';
import firebase from '../../utils/firebase';
import * as commonServices from '../../services';
import * as actions from './actions';

export function* fetchNewsletter() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_NEWSLETTER_REQUEST);

      const { nid, sid } = action.payload;
      const newsletterRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/school-newsletters/${nid}`);
      const newsletterPdfRef = yield call(firebase.storage.ref, `${FIREBASE_REF}/schools/${sid}/school-newsletters/${nid}.pdf`);

      const [doc, downloadURL] = yield all([
        call(firebase.firestore.getDocument, newsletterRef),
        call(firebase.storage.getDownloadURL, newsletterPdfRef),
      ]);

      const { data: blob } = yield commonServices.fetchBlob(downloadURL);

      const newsletter = doc.exists ? doc.data() : null;

      yield put(actions.fetchNewsletterSuccess({ newsletter: { ...newsletter, id: nid, blob } }));
    } catch (error) {
      yield put(actions.fetchNewsletterFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchSurvey() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_SURVEY_REQUEST);

      const { nid } = action.payload;
      const surveyRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/surveys/${nid}`);
      const doc = yield call(firebase.firestore.getDocument, surveyRef);

      const survey = doc.exists ? doc.data() : null;

      yield put(actions.fetchSurveySuccess({ survey }));
    } catch (error) {
      yield put(actions.fetchSurveyFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchIndividual() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_INDIVIDUAL_REQUEST);

      const { gcid, nid } = action.payload;
      const individualRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/individuals/${nid}`);
      const doc = yield call(firebase.firestore.getDocument, individualRef);

      const individuals = doc.exists ? doc.data() : null;

      const individual = getOr(null, `data.${gcid}`, individuals);

      yield put(actions.fetchIndividualSuccess({ individual: { ...individual, answers: individual && values(individual.answers) } }));
    } catch (error) {
      yield put(actions.fetchIndividualFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchTarget() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_TARGET_REQUEST);

      const { gcid } = action.payload;

      const { data: target } = yield call(contactServices.fetchGuardianContact, { gcid });

      const { data: contact } = yield call(contactServices.fetchContact, { cid: target.contact_id });

      yield put(actions.fetchTargetSuccess({ target: { ...target, contact } }));
    } catch (error) {
      yield put(actions.fetchTargetFailure({ error }));

      if (get('response.status', error) !== 403) {
        yield put(errorActions.errorOccured({ error }));
      }
    }
  }
}

export function* createResponse() {
  while (true) {
    try {
      const action = yield take(actions.CREATE_RESPONSE_REQUEST);

      const {
        gcid, nid, answers, signatures,
      } = action.payload;

      const newsletterRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/school-newsletters/${nid}`);
      const statsRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/stats/${nid}`);

      const newsletterDoc = yield call([newsletterRef, 'get']);

      if (get('status', newsletterDoc.data()) === 'closed') {
        yield put(actions.createResponseFailure({ error: { message: '이미 마감된 가정통신문입니다' } }));
      } else {
        const updateFunction = {
          newsletter: async (transaction) => {
            const doc = await transaction.get(newsletterRef);

            if (!doc.exists) {
              throw new Error('Document does not exist.');
            }

            const newsletter = doc.data();

            transaction.update(newsletterRef, {
              recordCount: newsletter.recordCount + 1,
              targets: { ...newsletter.targets, [gcid]: true },
            });
          },
          stats: async (transaction) => {
            const doc = await transaction.get(statsRef);

            if (!doc.exists) {
              const newStats = reduce.convert({ cap: false })((acc, cur, key) => ({
                ...acc,
                [key + 1]: isArray(cur)
                  ? reduce((a, c) => ({
                    ...a,
                    ...(includes('other:', c) ? { others: { [last(split('other:', c))]: 1 } } : { [c]: 1 }),
                  }), {}, cur)
                  : {
                    ...(cur && (includes('other:', cur) ? { others: { [last(split('other:', cur))]: 1 } } : { [cur]: 1 })),
                  },
              }), {}, answers);

              transaction.set(statsRef, newStats, { merge: true });
            } else {
              const stats = doc.data();

              const newStats = {
                ...stats,
                ...reduce.convert({ cap: false })((acc, cur, key) => ({
                  ...acc,
                  [key + 1]: isArray(cur)
                    ? {
                      ...stats[key + 1],
                      ...reduce((a, c) => ({
                        ...a,
                        ...(includes('other:', c)
                          ? {
                            others: {
                              ...getOr({}, 'others', stats[key + 1]),
                              [last(split('other:', c))]: getOr(0, `others.${last(split('other:', c))}`, stats[key + 1]) + 1,
                            },
                          }
                          : { [c]: getOr(0, c, stats[key + 1]) + 1 }),
                      }), {}, cur),
                    }
                    : {
                      ...stats[key + 1],
                      ...(cur && includes('other:', cur)
                        ? {
                          others: {
                            ...getOr({}, 'others', stats[key + 1]),
                            [last(split('other:', cur))]: getOr(0, `others.${last(split('other:', cur))}`, stats[key + 1]) + 1,
                          },
                        }
                        : cur && { [cur]: getOr(0, cur, stats[key + 1]) + 1 }),
                    },
                }), {}, answers),
              };

              transaction.set(statsRef, newStats, { merge: true });
            }
          },
        };

        const individualRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/individuals/${nid}`);
        const targetRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/targets/${nid}`);
        const updatedAt = yield call(firebase.firestore.FieldValue.serverTimestamp);

        yield all([
          call(firebase.firestore.runTransaction, updateFunction.newsletter),
          call(firebase.firestore.runTransaction, updateFunction.stats),
          call(firebase.firestore.setDocument, individualRef, {
            data: {
              [gcid]: {
                updatedAt,
                answers: reduce.convert({ cap: false })((acc, cur, key) => ({
                  ...acc,
                  [key + 1]: cur,
                }), {}, answers),
                ...(signatures && {
                  signatures: reduce.convert({ cap: false })((acc, cur, key) => ({
                    ...acc,
                    [key + 1]: cur,
                  }), {}, signatures),
                }),
              },
            },
          }, { merge: true }),
          call(firebase.firestore.updateDocument, targetRef, {
            [`data.${gcid}.recorded`]: true,
          }),
        ]);

        yield put(actions.createResponseSuccess());
      }
    } catch (error) {
      yield put(actions.createResponseFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* updateResponse() {
  while (true) {
    try {
      const action = yield take(actions.UPDATE_RESPONSE_REQUEST);

      const {
        gcid, nid, answers, signatures,
      } = action.payload;

      const newsletterRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/school-newsletters/${nid}`);
      const statsRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/stats/${nid}`);

      const newsletterDoc = yield call([newsletterRef, 'get']);

      if (get('status', newsletterDoc.data()) === 'closed') {
        yield put(actions.updateResponseFailure({ error: { message: '이미 마감된 가정통신문입니다' } }));
      } else {
        const individual = yield select((state) => state.newsletter.individual);

        const updateFunction = async (transaction) => {
          const doc = await transaction.get(statsRef);

          if (!doc.exists) {
            throw new Error('Document does not exist.');
          }

          const stats = doc.data();

          const removedStats = mapValues.convert({ cap: false })((value, key) => {
            const othersKey = compose(
              last,
              split('other:'),
              (val) => (isArray(val) ? find(includes('other:'), val) : val),
              get(`answers.${toNumber(key) - 1}`),
            )(individual.data);

            return pickBy(identity, (
              isArray(get(`answers.${toNumber(key) - 1}`, individual.data))
                ? {
                  ...value,
                  ...reduce((acc, cur) => ({
                    ...acc,
                    ...(includes('other:', cur)
                      ? {
                        others: pickBy(identity, {
                          ...value.others,
                          [othersKey]: get(othersKey, value.others) - 1,
                        }),
                      }
                      : {
                        [cur]: value[cur] - 1,
                      }),
                  }), {}, get(`answers.${toNumber(key) - 1}`, individual.data)),
                }
                : {
                  ...value,
                  ...(includes('other:', get(`answers.${toNumber(key) - 1}`, individual.data))
                    ? {
                      others: pickBy(identity, {
                        ...value.others,
                        [othersKey]: get(othersKey, value.others) - 1,
                      }),
                    }
                    : {
                      [get(`answers.${toNumber(key) - 1}`, individual.data)]: value[get(`answers.${toNumber(key) - 1}`, individual.data)] - 1,
                    }),
                }
            ));
          }, stats);

          const newStats = {
            ...stats,
            ...reduce.convert({ cap: false })((acc, cur, key) => ({
              ...acc,
              [key + 1]: isArray(cur)
                ? {
                  ...removedStats[key + 1],
                  ...reduce((a, c) => ({
                    ...a,
                    ...(includes('other:', c)
                      ? {
                        others: {
                          ...getOr({}, 'others', removedStats[key + 1]),
                          [last(split('other:', c))]: getOr(0, `others.${last(split('other:', c))}`, removedStats[key + 1]) + 1,
                        },
                      }
                      : { [c]: getOr(0, c, removedStats[key + 1]) + 1 }),
                  }), {}, cur),
                }
                : {
                  ...removedStats[key + 1],
                  ...(cur && includes('other:', cur)
                    ? {
                      others: {
                        ...getOr({}, 'others', removedStats[key + 1]),
                        [last(split('other:', cur))]: getOr(0, `others.${last(split('other:', cur))}`, removedStats[key + 1]) + 1,
                      },
                    }
                    : cur && { [cur]: getOr(0, cur, removedStats[key + 1]) + 1 }),
                },
            }), {}, answers),
          };

          transaction.update(statsRef, newStats);
        };

        const individualRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/individuals/${nid}`);
        const updatedAt = yield call(firebase.firestore.FieldValue.serverTimestamp);

        yield all([
          call(firebase.firestore.runTransaction, updateFunction),
          call(firebase.firestore.updateDocument, individualRef, {
            [`data.${gcid}`]: {
              updatedAt,
              answers: reduce.convert({ cap: false })((acc, cur, key) => ({
                ...acc,
                [key + 1]: cur,
              }), {}, answers),
              ...(signatures && {
                signatures: reduce.convert({ cap: false })((acc, cur, key) => ({
                  ...acc,
                  [toNumber(key) + 1]: cur,
                }), {}, signatures),
              }),
            },
          }),
        ]);

        yield put(actions.updateResponseSuccess());
      }
    } catch (error) {
      yield put(actions.updateResponseFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}
